import React, { useMemo } from 'react';
import { TabContent, TabPane, Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'src/components/ui';
import { EmptyState } from 'src/components/Icons/EmptyStates';
import Jnx, { useJnx } from '../../../../util/jnx';
import getPathFromId from '../../../../util/getPathFromId';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Icon from 'src/components/Icon';

class DropdownSelectLayout {
  constructor({ $id, hasToolbar, tabNameExpr, itemsAreObjectType, itemsUiSchema, onEmptyMessage, positionButtons, rowClassNames, openModal, tabs }) {
    this.$id = $id;
    this.hasToolbar = hasToolbar;
    this.itemsAreObjectType = itemsAreObjectType;
    this.itemsUiSchema = itemsUiSchema;
    this.tabNameJnx = tabNameExpr ? new Jnx(tabNameExpr) : null;
    this.onEmptyMessage = onEmptyMessage;
    this.positionButtons = positionButtons;
    this.rowClassNames = rowClassNames;
    this.openModal = openModal;
    this.tabs = tabs;
  }

  wrapArray(children, { selectedItem, setSelectedItem, props, onAddClick, $id, rootFormData, formContext }) {
    selectedItem = selectedItem ?? 0;
    const {
      'ui:addable': uiAddable = true,
      'ui:removable': removable,
      'ui:removable-label': removableLabel,
      'ui:removableConfirm': removableConfirm,
      'ui:removableConfirmText': removableConfirmText,
    } = this.itemsUiSchema || {};

    const hasAddBtn = props.canAdd && uiAddable;

    const CONFIRM_REMOVE_ITEM = () => [
      'ConfirmationModal',
      '',
      removableConfirmText || t`Confirm that you want to delete the entered information`,
      [
        { text: t`Yes`, color: 'primary', value: true },
        { text: t`No`, color: 'secondary', value: false },
      ],
    ];

    if (this.tabs.length === 0) {
      if (uiAddable) {
        return (
          <Button color="primary" onClick={onAddClick} disabled={props.disabled || props.readonly}>
            <Icon icon="Plus" weight="regular" /> {this.itemsUiSchema['ui:addButtonText'] ? ` ${this.itemsUiSchema['ui:addButtonText']}` : <Trans>Add Item</Trans>}
          </Button>
        );
      }

      if (this.onEmptyMessage) {
        return <EmptyState icon="inbox" size={96} message={this.onEmptyMessage} />;
      }
    }

    const selectedTab = this.tabs[selectedItem];

    return (
      <>
        <div className="btn-group flex-h">
          <UncontrolledDropdown>
            <DropdownToggle className="simple-dropdown-toggle" caret>
              <TabName jnx={this.tabNameJnx} index={selectedItem} tab={selectedTab} $id={$id} rootFormData={rootFormData} formContext={formContext} />
            </DropdownToggle>
            <DropdownMenu>
              {this.tabs.map((tab, index) => {
                const itemprops = props.items[index] || {};
                return (
                  <DropdownItem key={index} onClick={() => setSelectedItem(index)} active={index === selectedItem}>
                    <TabName jnx={this.tabNameJnx} index={index} tab={tab} $id={$id} rootFormData={rootFormData} formContext={formContext} />
                    {removable && index === selectedItem ? (
                      <Button
                        color="danger"
                        aria-label="Remove"
                        className="remove-btn"
                        tabIndex="-1"
                        style={{
                          flex: '0 0 32px',
                          paddingLeft: 6,
                          paddingRight: 6,
                          fontWeight: 'bold',
                        }}
                        disabled={itemprops.disabled || itemprops.readonly}
                        onClick={
                          removableConfirm
                            ? async () => {
                                if (await this.openModal(...CONFIRM_REMOVE_ITEM())) {
                                  await itemprops.onDropIndexClick(itemprops.index);
                                }
                              }
                            : async () => {
                                await itemprops.onDropIndexClick(itemprops.index);
                              }
                        }
                      >
                        {removableLabel || <Icon icon="X" weight="regular" />}
                      </Button>
                    ) : null}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </UncontrolledDropdown>
          {hasAddBtn && (
            <Button onClick={onAddClick} disabled={props.disabled || props.readonly}>
              <Icon icon="Plus" weight="regular" />{' '}
            </Button>
          )}
        </div>
        <TabContent className="form-tab-content" activeTab={selectedItem}>
          {children}
        </TabContent>
      </>
    );
  }

  wrapBody(children) {
    return children;
  }

  wrapLoader(children) {
    return children;
  }

  makeHeaders() {
    return null;
  }

  makeEmptyMessage() {
    return <EmptyState size={96} message={this.onEmptyMessage} />;
  }

  wrapItemErrorList(children) {
    return children;
  }

  wrapItemField({ name, uiSchema }, children) {
    return (
      <div key={name} className={(uiSchema[name] || {})['ui:cellClassName']}>
        {children}
      </div>
    );
  }

  wrapItem(id, props, onAddClick, rowIdx, rowCount, children, buttons, rowClassNames) {
    return (
      <TabPane tabId={rowIdx} key={rowIdx}>
        <div id={id} className={`tab-pane-item ${props.className} ${rowClassNames}`}>
          {children}
        </div>
      </TabPane>
    );
  }

  wrapAddButton() {}

  makeSimpleItem(props) {
    return <div>{props.children}</div>;
  }
}

function TabName({ $id, rootFormData, formContext, jnx, index, tab }) {
  const path = useMemo(() => `${getPathFromId($id)}.${index}`, [$id, index]);

  return jnx
    ? jnx.eval(rootFormData, path, {
        index,
        tab,
        formContext,
      })
    : `Tab ${index}`;
}

export default DropdownSelectLayout;
